import React, { useState, useCallback } from 'react';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Drawer, message } from 'antd';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@frontend/shadcn/components/ui/select';
import { EllipsisIcon } from '@revfluence/fresh-icons/solid/esm';
import { LockIcon, PenIcon } from '@revfluence/fresh-icons/regular';
import { PlusIcon, XmarkIcon, CheckIcon } from '@revfluence/fresh-icons/regular/esm';
import Tag from '@frontend/app/refresh-components/Tag';
import { Card } from '@frontend/shadcn/components/ui/card';
import { useAuth } from '@frontend/context/authContext';
import moment from 'moment';
import { isNil, groupBy } from 'lodash';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@frontend/shadcn/components/ui/dropdown-menu';
import './PayoutRulesDrawer.module.css';
import { ShoppingCartIcon } from 'lucide-react';
import { payoutUtcToLocal } from '@frontend/app/utils/date';
import { OFFER_PAYOUT_TYPE, PayoutRuleInput, PayoutRuleOperator, PRODUCT_CONDITION_TYPE, ORDER_VALUE_OPERATOR } from '../../types/globalTypes';
import { PayoutHeader } from './components/PayoutHeader';
import { PayoutConditions } from './components/PayoutConditions';
import PayoutTags from './components/PayoutTags';
import { PayoutCommissionInput } from './components/PayoutCommissionInput';
import { CONDITION_TYPE, ConditionType, IPayoutRules, OPERATOR_DISPLAY, PayoutAction, PayoutRuleConditionsInput, RuleCommission } from './components/types';
import { useShopifyShopQuery } from '../../hooks/useShopifyShopQuery';

// Maximum number of active payout rules
const MAX_PAYOUT_RULES = 5;

export const extractNumericGId = (gid: string) => {
  if (!gid) return '';
  // Handle both cases: with and without prefix
  const id = gid.startsWith('gid://shopify/Collection/') ? gid.split('/').pop() : gid;
  return id || '';
};

export const getPayoutText = (
  tempPayoutType: string,
  tempFlatPayout: number,
  tempPercentPayout: number
): React.ReactNode => {
  switch (tempPayoutType) {
    case OFFER_PAYOUT_TYPE.CONVERSION_AND_SALE:
      return (
        <>
          ${tempFlatPayout || 0} + {tempPercentPayout || 0}%
          <span className="text-grey-4"> commission</span>
        </>
      );

    case OFFER_PAYOUT_TYPE.SALE:
      return (
        <>
          {tempPercentPayout || 0}%
          <span className="text-grey-4"> commission</span>
        </>
      );

    case OFFER_PAYOUT_TYPE.CLICK:
    case OFFER_PAYOUT_TYPE.CONVERSION:
    case OFFER_PAYOUT_TYPE.THOUSAND_IMPRESSIONS:
      return (
        <>
          ${tempFlatPayout || 0} <span className="text-grey-4"> commission</span>
        </>
      );

    default:
      return '';
  }
};

export const hasIncompleteConditions = (condition: PayoutRuleConditionsInput) => {
  if (!condition.type) return false;

  switch (condition.type) {
    case ConditionType.COLLECTIONS:
      return !condition.collections || condition.collections.length === 0;
    case ConditionType.PRODUCT:
      return !condition.products || condition.products.length === 0;
    case ConditionType.ORDER_SUBTOTAL:
      return !condition.orderSubtotal || isNil(condition.orderSubtotal.value);
    case ConditionType.DATE_RANGE:
      return !condition.duration || !condition.duration.startDate || !condition.duration.endDate;
    case ConditionType.RECURRING_ORDER:
      return !condition.recurringLimit || (
        !condition.recurringLimit.isLimitPayoutToFirstOrder &&
        !condition.recurringLimit.isPayoutAppliedToAllRecurringOrders &&
        condition.recurringLimit.limitPayoutToMultipleRecurringOrder === null
      );
    case ConditionType.CUSTOMER_TYPE:
      return !condition.customerType;
    default:
      return false;
  }
};

const PayoutRulesDrawer: React.FC<IPayoutRules> = (props) => {
  const {
    open,
    onOpenChange,
    payout,
    formattedConversionType,
    payoutState,
    updatePayoutState,
    handleEditPayout,
    payoutIndex,
    payoutRules: initialPayoutRules,
    conditions: initialConditions,
    onPayoutRulesChange,
    onConditionsChange,
    clientsForSync,
    existingTitles = [],
  } = props;

  const shopifySubscriptionCheck = useShopifyShopQuery();
  const isShopifyConnected = shopifySubscriptionCheck?.data?.getShopifyStoreData?.isShopConnected || false;

  const [isEditing, setIsEditing] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isRuleEditing, setIsRuleEditing] = useState(false);
  const [editingRuleIndex, setEditingRuleIndex] = useState<number | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  // Add state for rule-specific commission values
  const [ruleCommission, setRuleCommission] = useState<RuleCommission>({
    flatPayout: 0,
    percentPayout: 0
  });

  // Initialize payoutRules with existing rules if they exist
  const [payoutRules, setPayoutRules] = useState<PayoutRuleInput[]>(() => {
    return initialPayoutRules || [];
  });

  // Initialize conditions based on the first rule if it exists
  const [conditions, setConditions] = useState<PayoutRuleConditionsInput[]>(() => {
    return initialConditions || [{ type: null, action: PayoutAction.ADD, id: null, operator: PayoutRuleOperator.AND }];
  });

  const { clientInfo } = useAuth();
  const clientId = clientInfo.id;

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleSave = useCallback(() => {
    handleEditPayout(payoutIndex);
    setIsEditing(false);
    setIsRuleEditing(false);
    onOpenChange(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleEditPayout, payoutIndex, onOpenChange, payout, payoutState, payoutRules]);

  const handleDeleteRule = useCallback((visibleIndex: number) => {
    // Find the actual index in the full array that corresponds to this visible index
    let currentVisibleIndex = 0;
    const actualIndex = payoutRules.findIndex((rule) => {
      if (rule.action !== PayoutAction.REMOVE) {
        if (currentVisibleIndex === visibleIndex) {
          return true;
        }
        currentVisibleIndex++;
      }
      return false;
    });

    if (actualIndex === -1) return;

    const updatedRules = [...payoutRules];
    updatedRules[actualIndex] = {
      ...updatedRules[actualIndex],
      action: PayoutAction.REMOVE
    };

    // Reset editing states when deleting a rule
    setIsRuleEditing(false);
    setEditingRuleIndex(null);

    // Update the rules state
    setPayoutRules(updatedRules);
    onPayoutRulesChange(updatedRules);
  }, [payoutRules, onPayoutRulesChange]);

  const addNewPayoutRule = useCallback(() => {
    const visibleRulesLength = payoutRules.filter((rule) => rule.action !== PayoutAction.REMOVE).length;

    const newRule = {
      id: null,
      action: null,
      collections: null,
      flatPayout: null,
      operator: PayoutRuleOperator.AND,
      orderSubtotal: null,
      percentPayout: null,
      duration: null,
      products: null,
      recurringLimit: null,
    };

    // Filter out any rules marked for removal before adding new rule
    const activeRules = payoutRules.filter((rule) => rule.action !== PayoutAction.REMOVE);
    const newRules = [...activeRules, newRule];

    setPayoutRules(newRules);
    onPayoutRulesChange(newRules);

    setEditingRuleIndex(visibleRulesLength);
    setIsRuleEditing(true);

    setRuleCommission({
      flatPayout: null,
      percentPayout: null
    });

    const newConditions = [{ type: null, action: PayoutAction.ADD, id: null, operator: PayoutRuleOperator.AND }];
    setConditions(newConditions);
    onConditionsChange(newConditions);
  }, [payoutRules, onPayoutRulesChange, onConditionsChange]);

  const handleConditionChange = useCallback((newConditions: PayoutRuleConditionsInput[]) => {
    setConditions(newConditions);
    onConditionsChange(newConditions);
  }, [onConditionsChange]);

  const handleConditionTypeChange = useCallback((value: CONDITION_TYPE, ruleIndex: number) => {
    const updatedRules = [...payoutRules];
    updatedRules[ruleIndex] = {
      ...updatedRules[ruleIndex],
      operator: value === CONDITION_TYPE.All_CONDITION ? PayoutRuleOperator.AND : PayoutRuleOperator.OR
    };
    setPayoutRules(updatedRules);
    onPayoutRulesChange(updatedRules);
  }, [payoutRules, onPayoutRulesChange]);

  const handleEditClick = (rule: PayoutRuleInput, index: number) => {
    setEditingRuleIndex(index);
    setIsRuleEditing(true);
    setRuleCommission({
      flatPayout: rule.flatPayout,
      percentPayout: rule.percentPayout
    });
    initializeConditionsForRule(rule);
  };

  const checkIncompleteConditions = useCallback(() => {
    return conditions.some((condition) => hasIncompleteConditions(condition));
  }, [conditions]);

  const handleSaveRule = useCallback(async () => {
    if (editingRuleIndex === null) return;

    if (checkIncompleteConditions()) {
      setShowErrors(true);
      message.error('Please complete the rule before saving');
      return;
    }

    const hasValidConditions = conditions.some((condition) => {
      switch (condition.type) {
        case ConditionType.COLLECTIONS:
          return condition.collections && condition.collections.length > 0;
        case ConditionType.PRODUCT:
          return condition.products && condition.products.length > 0;
        case ConditionType.ORDER_SUBTOTAL:
          return condition.orderSubtotal &&
                 !isNil(condition.orderSubtotal.value) &&
                 !isNil(condition.orderSubtotal.operator);
        case ConditionType.DATE_RANGE:
          return condition.duration &&
                 condition.duration.startDate &&
                 condition.duration.endDate;
        case ConditionType.RECURRING_ORDER:
          return condition.recurringLimit && (
            condition.recurringLimit.isLimitPayoutToFirstOrder === true ||
            condition.recurringLimit.isPayoutAppliedToAllRecurringOrders === true ||
            !isNil(condition.recurringLimit.limitPayoutToMultipleRecurringOrder)
          );
        case ConditionType.CUSTOMER_TYPE:
          return condition.customerType && (
            condition.customerType === 'NEW' || condition.customerType === 'RETURNING'
          );
        default:
          return false;
      }
    });

    // Check if commission values are valid based on payout type
    const hasValidCommission = (() => {
      switch (payout.payoutType) {
        case OFFER_PAYOUT_TYPE.SALE:
          return !isNil(ruleCommission.percentPayout) && ruleCommission.percentPayout >= 0 && ruleCommission.percentPayout <= 100;
        case OFFER_PAYOUT_TYPE.CONVERSION:
        case OFFER_PAYOUT_TYPE.CLICK:
          return !isNil(ruleCommission.flatPayout) && ruleCommission.flatPayout >= 0;
        case OFFER_PAYOUT_TYPE.CONVERSION_AND_SALE:
          return !isNil(ruleCommission.flatPayout) && ruleCommission.flatPayout >= 0 &&
                 !isNil(ruleCommission.percentPayout) && ruleCommission.percentPayout >= 0 && ruleCommission.percentPayout <= 100;
        default:
          return false;
      }
    })();

    if (!hasValidConditions) {
      message.error('Please add at least one condition before creating the rule');
      return;
    }

    if (!hasValidCommission) {
      message.error('Please enter a valid commission value for the rule');
      return;
    }

    const existingRule = payoutRules[editingRuleIndex];
    let action = PayoutAction.ADD;

    if (existingRule.id) {
      const hasChanges = (existingRule: PayoutRuleInput) => {
        // Compare commission values
        if (existingRule.flatPayout !== ruleCommission.flatPayout ||
            existingRule.percentPayout !== ruleCommission.percentPayout) {
          return true;
        }

        // Compare collections by ID and advertiserId
        const existingCollections = new Set(
          existingRule.collections?.map((c) => `${c.id}-${c.advertiserId}`) || []
        );
        const newCollections = new Set(
          conditions
            .filter((c) => c.type === ConditionType.COLLECTIONS)
            .flatMap((c) => c.collections || [])
            .map((c) => `${c.id}-${c.advertiserId}`)
        );
        if (existingCollections.size !== newCollections.size ||
            ![...existingCollections].every((id) => newCollections.has(id))) {
          return true;
        }

        // Compare products by ID, advertiserId, and operator
        const existingProducts = new Set(
          Array.isArray(existingRule.products) ? existingRule.products.map((p) => `${p.id}-${p.advertiserId}-${p.operator}`) : []
        );
        const newProducts = new Set(
          conditions
            .filter((c) => c.type === ConditionType.PRODUCT)
            .flatMap((c) => c.products || [])
            .map((p) => `${p.id}-${p.advertiserId}-${p.operator}`)
        );
        if (existingProducts.size !== newProducts.size ||
            ![...existingProducts].every((id) => newProducts.has(id))) {
          return true;
        }

        // Compare orderSubtotal directly
        const newOrderSubtotal = conditions.find((c) => c.type === ConditionType.ORDER_SUBTOTAL)?.orderSubtotal;
        if ((existingRule.orderSubtotal?.value !== newOrderSubtotal?.value) ||
            (existingRule.orderSubtotal?.operator !== newOrderSubtotal?.operator)) {
          return true;
        }

        // Compare duration dates directly
        const newDuration = conditions.find((c) => c.type === ConditionType.DATE_RANGE)?.duration;
        if ((existingRule.duration?.startDate?.toString() !== newDuration?.startDate?.toString()) ||
            (existingRule.duration?.endDate?.toString() !== newDuration?.endDate?.toString())) {
          return true;
        }

        // Compare recurring limit values directly
        const newRecurringLimit = conditions.find((c) => c.type === ConditionType.RECURRING_ORDER)?.recurringLimit;
        if ((existingRule.recurringLimit?.isLimitPayoutToFirstOrder !== newRecurringLimit?.isLimitPayoutToFirstOrder) ||
            (existingRule.recurringLimit?.isPayoutAppliedToAllRecurringOrders !== newRecurringLimit?.isPayoutAppliedToAllRecurringOrders) ||
            (existingRule.recurringLimit?.limitPayoutToMultipleRecurringOrder !== newRecurringLimit?.limitPayoutToMultipleRecurringOrder)) {
          return true;
        }
        const newCustomerType = conditions.find((c) => c.type === ConditionType.CUSTOMER_TYPE)?.customerType;
        if (existingRule.customerType !== newCustomerType) {
          return true;
        }
        return false;
      };

      action = hasChanges(existingRule) ? PayoutAction.UPDATE : PayoutAction.NOUPDATE;
    }

    // Gather all collections from conditions of type COLLECTIONS
    const allCollections = conditions
      .filter((condition) => condition.type === ConditionType.COLLECTIONS)
      .flatMap((condition) => condition.collections || [])
      .map((collection) => ({
        id: collection.id,
        name: collection.name,
        advertiserId: collection.advertiserId,
        operator: collection.operator
      }));

    // Accumulate all products from all product conditions
    const allProducts = conditions
      .filter((c) => c.type === ConditionType.PRODUCT && c.products)
      .flatMap((c) => c.products)
      .map((product) => ({
        id: product.id,
        name: product.name,
        type: PRODUCT_CONDITION_TYPE.PRODUCT_ID,
        imageUrl: product.imageUrl,
        advertiserId: product.advertiserId,
        operator: product.operator
      }));

    const newRule: PayoutRuleInput = {
      id: existingRule.id,
      action,
      collections: allCollections.length > 0 ? allCollections : null,
      flatPayout: ruleCommission.flatPayout,
      operator: existingRule.operator,
      orderSubtotal: conditions.find((c) => c.type === ConditionType.ORDER_SUBTOTAL)?.orderSubtotal || null,
      percentPayout: ruleCommission.percentPayout,
      duration: conditions.find((c) => c.type === ConditionType.DATE_RANGE)?.duration || null,
      products: allProducts.length > 0 ? allProducts : null,
      recurringLimit: conditions.find((c) => c.type === ConditionType.RECURRING_ORDER)?.recurringLimit || null,
      customerType: conditions.find((c) => c.type === ConditionType.CUSTOMER_TYPE)?.customerType || null,
    };

    const updatedRules = [...payoutRules];
    updatedRules[editingRuleIndex] = newRule;
    setPayoutRules(updatedRules);
    onPayoutRulesChange(updatedRules);
    setIsRuleEditing(false);
    setEditingRuleIndex(null);
  }, [editingRuleIndex, payoutRules, ruleCommission, conditions, onPayoutRulesChange, payout.payoutType, checkIncompleteConditions]);

  const initializeConditionsForRule = useCallback((rule: PayoutRuleInput) => {
    const newConditions: PayoutRuleConditionsInput[] = [];

    // Initialize collections condition if exists
    if (rule.collections && rule.collections.length > 0) {
      const collectionsByAdvertiser = groupBy(rule.collections, (collection) =>
        collection.advertiserId?.toString() || '0'
      );

      Object.entries(collectionsByAdvertiser).forEach(([advertiserId, collections]) => {
        // Ensure collections have the operator property
        const collectionsWithOperator = collections.map((collection) => ({
          ...collection,
          operator: collection.operator || ORDER_VALUE_OPERATOR.EQUAL_TO
        }));

        newConditions.push({
          type: ConditionType.COLLECTIONS,
          collections: collectionsWithOperator,
          action: PayoutAction.ADD,
          id: null,
          operator: PayoutRuleOperator.AND,
          selectedStoreId: advertiserId
        });
      });
    }

    // Initialize date range condition if exists
    if (rule.duration) {
      newConditions.push({
        type: ConditionType.DATE_RANGE,
        duration: {
          startDate: new Date(rule.duration.startDate),
          endDate: new Date(rule.duration.endDate)
        },
        action: PayoutAction.ADD,
        id: null,
        operator: PayoutRuleOperator.AND
      });
    }

    // Initialize order subtotal condition if exists
    if (rule.orderSubtotal?.value) {
      newConditions.push({
        type: ConditionType.ORDER_SUBTOTAL,
        orderSubtotal: {
          value: rule.orderSubtotal.value,
          operator: rule.orderSubtotal.operator
        },
        action: PayoutAction.ADD,
        id: null,
        operator: PayoutRuleOperator.AND
      });
    }

    // Initialize products condition if exists
    if (Array.isArray(rule.products) && rule.products.length > 0) {
      const productsByAdvertiser = groupBy(rule.products, (product) =>
        product.advertiserId?.toString() || '0'
      );

      // Create a separate condition for each advertiser's products
      Object.entries(productsByAdvertiser).forEach(([advertiserId, products]) => {
        // Ensure products have the operator property
        const productsWithOperator = products.map((product) => ({
          ...product,
          operator: product.operator || ORDER_VALUE_OPERATOR.EQUAL_TO
        }));

        newConditions.push({
          type: ConditionType.PRODUCT,
          products: productsWithOperator,
          action: PayoutAction.ADD,
          id: null,
          operator: PayoutRuleOperator.AND,
          selectedStoreId: advertiserId
        });
      });
    }

    // Initialize recurring order condition if exists
    if (rule.recurringLimit) {
      newConditions.push({
        type: ConditionType.RECURRING_ORDER,
        recurringLimit: {
          isLimitPayoutToFirstOrder: rule.recurringLimit.isLimitPayoutToFirstOrder,
          isPayoutAppliedToAllRecurringOrders: rule.recurringLimit.isPayoutAppliedToAllRecurringOrders,
          limitPayoutToMultipleRecurringOrder: rule.recurringLimit.limitPayoutToMultipleRecurringOrder
        },
        action: PayoutAction.ADD,
        id: null,
        operator: PayoutRuleOperator.AND
      });
    }

    // Initialize customer type condition if exists
    if (rule.customerType) {
      newConditions.push({
        type: ConditionType.CUSTOMER_TYPE,
        customerType: rule.customerType,
        action: PayoutAction.ADD,
        id: null,
        operator: PayoutRuleOperator.AND
      });
    }

    // If no conditions exist, add an empty condition
    if (newConditions.length === 0) {
      newConditions.push({ type: null, action: PayoutAction.ADD, id: null, operator: PayoutRuleOperator.AND });
    }

    setConditions(newConditions);
    onConditionsChange(newConditions);
  }, [onConditionsChange]);

  const canClosePayout = useCallback(() => {
    if (isRuleEditing) return false;
    if (checkIncompleteConditions()) return false;
    return true;
  }, [isRuleEditing, checkIncompleteConditions]);

  const handleCancelRule = () => {
    // Case 1: For a completely new rule that hasn't been added to payoutRules yet
    // This happens when user clicks "Add Rule" but hasn't clicked the checkmark yet
    if (editingRuleIndex === payoutRules.filter((rule) => rule.action !== PayoutAction.REMOVE).length - 1 &&
        !payoutRules[editingRuleIndex]?.action &&
        !payoutRules[editingRuleIndex]?.id) {
      const updatedRules = payoutRules.slice(0, -1);
      setPayoutRules(updatedRules);
      onPayoutRulesChange(updatedRules);

      setConditions([{ type: null, action: PayoutAction.ADD, id: null, operator: PayoutRuleOperator.AND }]);
      onConditionsChange([{ type: null, action: PayoutAction.ADD, id: null, operator: PayoutRuleOperator.AND }]);

      setRuleCommission({
        flatPayout: 0,
        percentPayout: 0
      });
    // Case 2: For an existing rule being edited (either saved to backend or just frontend)
    // This happens when user clicks edit on an existing rule
    } else if (editingRuleIndex !== null && (payoutRules[editingRuleIndex]?.action === 'ADD' || payoutRules[editingRuleIndex]?.id)) {
      const existingRule = payoutRules[editingRuleIndex];

      setRuleCommission({
        flatPayout: existingRule.flatPayout || 0,
        percentPayout: existingRule.percentPayout || 0
      });

      const savedConditions = [];

      if (existingRule.collections?.length > 0) {
        // Ensure collections have the operator property
        const collectionsWithOperator = existingRule.collections.map((collection) => ({
          ...collection,
          operator: collection.operator || ORDER_VALUE_OPERATOR.EQUAL_TO
        }));

        savedConditions.push({
          type: ConditionType.COLLECTIONS,
          collections: collectionsWithOperator,
          action: PayoutAction.ADD,
          id: existingRule.id
        });
      }

      if (Array.isArray(existingRule.products) && existingRule.products.length > 0) {
        // Ensure products have the operator property
        const productsWithOperator = existingRule.products.map((product) => ({
          ...product,
          operator: product.operator || ORDER_VALUE_OPERATOR.EQUAL_TO
        }));

        savedConditions.push({
          type: ConditionType.PRODUCT,
          products: productsWithOperator,
          action: PayoutAction.ADD,
          id: existingRule.id
        });
      }

      if (existingRule.orderSubtotal?.value > 0) {
        savedConditions.push({
          type: ConditionType.ORDER_SUBTOTAL,
          orderSubtotal: existingRule.orderSubtotal,
          action: PayoutAction.ADD,
          id: existingRule.id
        });
      }

      if (existingRule.duration?.startDate && existingRule.duration?.endDate) {
        savedConditions.push({
          type: ConditionType.DATE_RANGE,
          duration: existingRule.duration,
          action: PayoutAction.ADD,
          id: existingRule.id
        });
      }

      if (existingRule.recurringLimit) {
        savedConditions.push({
          type: ConditionType.RECURRING_ORDER,
          recurringLimit: existingRule.recurringLimit,
          action: PayoutAction.ADD,
          id: existingRule.id,
          operator: PayoutRuleOperator.AND
        });
      }

      if (existingRule.customerType) {
        savedConditions.push({
          type: ConditionType.CUSTOMER_TYPE,
          customerType: existingRule.customerType,
          action: PayoutAction.ADD,
          id: existingRule.id,
          operator: PayoutRuleOperator.AND
        });
      }

      // If no saved conditions exist, add empty condition
      if (savedConditions.length === 0) {
        savedConditions.push({ type: null, action: PayoutAction.ADD, id: null });
      }

      setConditions(savedConditions);
      onConditionsChange(savedConditions);
    }

    setIsRuleEditing(false);
    setEditingRuleIndex(null);
  };

  // Update the commission change handler
  const handleCommissionChange = useCallback((value: number, isPercent: boolean) => {
    setRuleCommission((prev) => {
      const next = {
        ...prev,
        [isPercent ? 'percentPayout' : 'flatPayout']: value
      };
      return next;
    });
  }, []);

  return (
    <div>
      <Drawer
        open={open}
        onClose={handleClose}
        title="Payout Rules"
        width={600}
        bodyStyle={{
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
        headerStyle={{ fontSize: '20px', padding: '16px' }}
        className="payout-rules-drawer"
      >
        {/* Main content wrapper */}
        <div className="flex flex-col h-full">
          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto">
            <PayoutHeader
              isEditingTitle={isEditingTitle}
              setIsEditingTitle={setIsEditingTitle}
              payoutState={payoutState}
              updatePayoutState={updatePayoutState}
              formattedConversionType={formattedConversionType}
              isRuleEditing={isRuleEditing}
              existingTitles={existingTitles}
              currentTitle={payout?.label}
            />

            <div className="px-4 py-2 space-y-4">
              {payoutRules
                .filter((rule) => rule.action !== PayoutAction.REMOVE)
                .map((rule, visibleIndex) => (
                  <React.Fragment key={rule.id || visibleIndex}>
                    {isRuleEditing && editingRuleIndex === visibleIndex ? (
                      <Card className="bg-grey-0 rounded-xl border border-grey-2 p-0 my-2">
                        <div className="py-2 px-3 bg-grey-1 flex justify-between items-center rounded-xl">
                          <div className="w-[90%]">
                            <PayoutCommissionInput
                              payoutType={payoutState.tempPayoutType as OFFER_PAYOUT_TYPE}
                              commissionValue={ruleCommission}
                              onCommissionChange={handleCommissionChange}
                            />
                          </div>
                          <div className="w-[10%] flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="smallIcon"
                              type="button"
                              onClick={handleCancelRule}
                              className="cursor-pointer transition-all duration-300 ease-in-out hover:bg-grey-1 hover:border-grey-5 hover:text-grey-5"
                            >
                              <XmarkIcon />
                            </Button>
                            <Button
                              size="smallIcon"
                              type="button"
                              onClick={handleSaveRule}
                              className="cursor-pointer transition-all duration-300 ease-in-out"
                            >
                              <CheckIcon />
                            </Button>
                          </div>
                        </div>
                        <div className="p-3 m-0">
                          <div className="flex items-center gap-1">
                            <p className="m-0 text-sm text-grey-5">Order should follow</p>
                            <Select
                              value={
                                rule.operator === PayoutRuleOperator.AND
                                  ? CONDITION_TYPE.All_CONDITION
                                  : CONDITION_TYPE.ANY_CONDITION
                              }
                              onValueChange={(value) =>
                                handleConditionTypeChange(value as CONDITION_TYPE, visibleIndex)}
                            >
                              <SelectTrigger className="w-max border-0 font-semibold text-sm text-grey-5 focus:outline-none focus:ring-0 h-6 p-0 underline">
                                <SelectValue placeholder="Select Payout Type" />
                              </SelectTrigger>
                              <SelectContent className="z-[9999]">
                                <SelectItem value={CONDITION_TYPE.All_CONDITION}>
                                  {CONDITION_TYPE.All_CONDITION}
                                </SelectItem>
                                <SelectItem value={CONDITION_TYPE.ANY_CONDITION}>
                                  {CONDITION_TYPE.ANY_CONDITION}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <PayoutConditions
                            clientId={clientId}
                            conditions={conditions}
                            onConditionsChange={handleConditionChange}
                            showErrors={showErrors}
                            isShopifyConnected={isShopifyConnected}
                            clientsForSync={clientsForSync}
                          />
                        </div>
                      </Card>
                    ) : (
                      <Card
                        className={`bg-grey-2 border border-grey-2 p-0 rounded-xl ${
                          isRuleEditing && editingRuleIndex === visibleIndex ? 'border-t border-primary' : ''
                        }`}
                      >
                        <div className="py-2 px-3 bg-grey-0 h-10 flex justify-between items-center rounded-xl">
                          <div className="width-[90%]">
                            {getPayoutText(payoutState.tempPayoutType, rule.flatPayout, rule.percentPayout)}
                          </div>
                          <div className="width-[10%] flex gap-2">
                            <Button
                              variant="outline"
                              size="smallIcon"
                              type="button"
                              onClick={() => handleEditClick(rule, visibleIndex)}
                              disabled={isEditing || isRuleEditing || isEditingTitle}
                              className="cursor-pointer transition-all duration-300 ease-in-out hover:border hover:text-grey-5 border-0 bg-grey-1"
                            >
                              <PenIcon fontSize={12} />
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger className="w-max-content rounded-lg font-normal text-primary p-1 h-6 focus:outline-none focus:ring-0 transition-all duration-300 ease-in-out border border-grey-1 bg-grey-1 hover:border-grey-5 hover:text-grey-5">
                                <EllipsisIcon className="w-3 h-3 text-grey-6" />
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="z-[9999]">
                                <DropdownMenuItem
                                  onClick={() => handleDeleteRule(visibleIndex)}
                                  className="text-red-500 cursor-pointer"
                                  disabled={isEditingTitle || isRuleEditing}
                                >
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 px-3 py-2">
                          <PayoutTags
                            tags={[
                              ...(rule.collections?.map((collection) => ({
                                id: extractNumericGId(collection.id),
                                name: collection.name,
                                type: 'collection' as const,
                              })) || []),
                              ...(Array.isArray(rule.products) ? rule.products.map((product) => ({
                                id: String(product.id),
                                name: product.name,
                                type: 'product' as const,
                                imageUrl: product.imageUrl,
                              })) : []),
                              ...(rule.orderSubtotal?.value > 0
                                ? [
                                    {
                                      id: 'orderSubtotal',
                                      name: `Order Subtotal ${OPERATOR_DISPLAY[rule.orderSubtotal.operator]} $${
                                        rule.orderSubtotal.value
                                      }`,
                                      type: 'order_subtotal' as const,
                                    },
                                  ]
                                : []),
                              ...(rule.duration
                                ? [
                                    {
                                      id: 'dateRange',
                                      name: `${moment(payoutUtcToLocal(rule.duration.startDate)).format('MMM D')} - ${moment(
                                        payoutUtcToLocal(rule.duration.endDate),
                                      ).format('MMM D, YYYY')}`,
                                      type: 'date_range' as const,
                                    },
                                  ]
                                : []),
                              ...(rule.recurringLimit
                                ? [
                                    {
                                      id: 'recurring',
                                      name: ConditionType.RECURRING_ORDER,
                                      type: 'recurring' as const,
                                      recurringLimit: {
                                        isLimitPayoutToFirstOrder: rule.recurringLimit.isLimitPayoutToFirstOrder,
                                        isPayoutAppliedToAllRecurringOrders:
                                          rule.recurringLimit.isPayoutAppliedToAllRecurringOrders,
                                        limitPayoutToMultipleRecurringOrder:
                                          rule.recurringLimit.limitPayoutToMultipleRecurringOrder,
                                      },
                                    },
                                  ]
                                : []),
                              ...(rule.customerType
                                ? [
                                    {
                                      id: 'customerType',
                                      name: rule.customerType === 'NEW' ? 'New Customers' : 'Returning Customers',
                                      type: 'customer_type' as const,
                                    },
                                  ]
                                : []),
                            ]}
                            showDivider
                          />
                        </div>
                      </Card>
                    )}
                  </React.Fragment>
                ))}
            </div>

            <div className="m-4 mt-0">
              <Button
                variant="ghost"
                className={`flex items-center mb-2 gap-2 p-0 h-8 ${
                  isEditing || isRuleEditing || isEditingTitle || payoutRules.filter((rule) => rule.action !== PayoutAction.REMOVE).length >= MAX_PAYOUT_RULES
                    ? 'cursor-not-allowed opacity-50'
                    : ''
                } border border-dashed !border-grey-3 w-full`}
                onClick={addNewPayoutRule}
                disabled={isEditing || isRuleEditing || isEditingTitle || payoutRules.filter((rule) => rule.action !== PayoutAction.REMOVE).length >= MAX_PAYOUT_RULES}
              >
                <PlusIcon />
                <p className="m-0 text-sm font-medium text-primary">
                  {payoutRules.filter((rule) => rule.action !== PayoutAction.REMOVE).length >= MAX_PAYOUT_RULES
                    ? 'No new rules can be added'
                    : 'Add Rule'}
                </p>
              </Button>
            </div>

            {/* Everything else section */}
            <div className="px-4 mt-4 mb-20">
              <Card className="bg-grey-2 border border-grey-2 p-0 rounded-xl">
                <div className="py-2 px-3 bg-grey-0 h-10 flex justify-between items-center rounded-xl">
                  <div className="flex items-center gap-4 w-[90%]">
                    {isEditing ? (
                      <PayoutCommissionInput
                        payoutType={payoutState.tempPayoutType}
                        commissionValue={{
                          flatPayout: payoutState.tempFlatPayout,
                          percentPayout: payoutState.tempPercentPayout,
                        }}
                        onCommissionChange={(value, isPercent) => {
                          updatePayoutState(isPercent ? 'tempPercentPayout' : 'tempFlatPayout', value);
                        }}
                      />
                    ) : (
                      <div className="font-medium">
                        {getPayoutText(
                          payoutState.tempPayoutType,
                          payoutState.tempFlatPayout,
                          payoutState.tempPercentPayout,
                        )}
                      </div>
                    )}
                  </div>
                  <div className="width-[10%] flex gap-2">
                    {isEditing ? (
                      <>
                        <Button
                          variant="outline"
                          size="smallIcon"
                          onClick={() => setIsEditing(false)}
                          className="cursor-pointer transition-all duration-300 ease-in-out hover:bg-grey-1 hover:border-grey-5 hover:text-grey-5"
                        >
                          <XmarkIcon />
                        </Button>
                        <Button
                          size="smallIcon"
                          onClick={() => setIsEditing(false)}
                          className="cursor-pointer transition-all duration-300 ease-in-out"
                        >
                          <CheckIcon />
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="outline"
                        size="smallIcon"
                        onClick={() => setIsEditing(true)}
                        disabled={isRuleEditing || isEditingTitle}
                        className="cursor-pointer transition-all duration-300 ease-in-out hover:border hover:text-grey-5 border-0 bg-grey-1"
                      >
                        <PenIcon fontSize={12} />
                      </Button>
                    )}
                  </div>
                </div>
                {payoutRules.length > 0 ? (
                  <div className="flex items-center gap-2 px-3 py-2">
                    <span className="text-sm text-grey-6 mr-1 whitespace-nowrap">Applicable on:</span>
                    <Tag
                      icon={<LockIcon className="w-3.5 h-3.5" />}
                      label="Everything Else"
                      bgColor="bg-yellow-6"
                      textColor="text-white"
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-2 px-3 py-2">
                    <span className="text-sm text-grey-6 mr-1 whitespace-nowrap">Applicable on:</span>
                    <Tag
                      icon={<ShoppingCartIcon className="w-3.5 h-3.5" />}
                      label="All Products"
                      bgColor="bg-purple-4"
                      textColor="text-white"
                    />
                  </div>
                )}
              </Card>
            </div>
          </div>

          {/* Sticky footer */}
          <div className="sticky bottom-0 border-t border-grey-2 bg-white mt-auto">
            <div className="px-6 py-4">
              <Button variant="outline" onClick={handleSave} disabled={!canClosePayout()}>
                Done
              </Button>
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default React.memo(PayoutRulesDrawer);
